import type { Dispatch, SetStateAction } from 'react';

import { todayInZone } from './private-event-dates';
import type { PrivateEventFormFields } from './private-event-form-state';
import { stepOfField } from './private-event-payload';
import { errorsForStep, type StepErrors } from './private-event-validation';

export const STEP_COUNT = 3;

function withoutStep(errors: StepErrors, step: number): StepErrors {
  return Object.fromEntries(Object.entries(errors).filter(([k]) => stepOfField(k) !== step));
}

const isValid = (errs: StepErrors) => Object.keys(errs).length === 0;

/** Per-step validation for the drawer's nav guard and its Create button. "Today"
 *  is read when a step is checked, in the event's own zone. */
export function useStepValidation(
  fields: PrivateEventFormFields,
  eventZone: string,
  step: number,
  setFieldErrors: Dispatch<SetStateAction<StepErrors>>,
) {
  const errorsOf = (s: number) => errorsForStep(s, fields, todayInZone(eventZone));

  /** Writes the step's inline errors and reports whether it is valid. */
  const validateStep = (target = step): boolean => {
    const errs = errorsOf(target);
    setFieldErrors((prev) => ({ ...withoutStep(prev, target), ...errs }));
    return isValid(errs);
  };

  const stepReachable = (target: number): boolean =>
    Array.from({ length: target }, (_, i) => i).every((i) => isValid(errorsOf(i)));

  /** The first failing step, with every step's errors written; null when valid. */
  const firstInvalidStep = (): number | null => {
    const all = Array.from({ length: STEP_COUNT }, (_, i) => errorsOf(i));
    const failing = all.findIndex((errs) => !isValid(errs));
    if (failing < 0) return null;
    setFieldErrors(Object.assign({}, ...all));
    return failing;
  };

  return { validateStep, stepReachable, firstInvalidStep };
}
